import React, { useMemo } from 'react'

import { Button, DataTable, DataTableColumn, Grid, Radio, TextField, Typography } from '@barracuda-internal/bds-core'

import { useFormatMessage } from 'lib/localization'
import { FormSpacer } from 'components/libs/layout/pages/pageLayout'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'
import { AvailableSettings, BulkEmailExemptPolicy } from 'types/Settings'
import { Cell } from 'components/libs/grid/cell'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import { TableText } from 'components/libs/layout/pages/TableText'
import styles from 'components/pages/inboundSettings/inboundSettingsStyles'
import { BulkEmailExemptProps, INPUT_NAMES, useBulkEmailExemptTableLogic } from './useBulkEmailExemptTableLogic'

const BASE_I18N_KEY = 'ess.inbound_settings.reputation'
const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'

const BulkEmailExempt = (props: BulkEmailExemptProps) => {
  const isReadonly = useIsReadonly()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)
  const inboundClasses = styles()
  const [state, eventHandlers] = useBulkEmailExemptTableLogic(props)
  return useMemo(
    () => (
      <Grid container>
        <Typography variant="h4">{formatMessage('bulk_email_exemptions')}</Typography>
        <FormSpacer />
        <Button
          className={inboundClasses.bulkEdit}
          onClick={eventHandlers.onBulkEdit}
          variant="contained"
          size="small"
          data-testid="bulk-button-ip"
          disabled={isReadonly}
        >
          {formatInboundMessage('bulk_edit')}
        </Button>
        <Grid data-testid="ip-address-table-grid">
          <DataTable
            sortable={{
              allowUnsort: false,
              mode: 'single'
            }}
            sort={state.sort}
            onSortChange={eventHandlers.handleSortChange}
            data={state.tableData}
          >
            <DataTableColumn
              sortable
              field={INPUT_NAMES.NAME}
              title={formatMessage('email_domain')}
              cell={({ dataItem }: { dataItem: BulkEmailExemptPolicy }) => (
                <Cell>
                  {!dataItem.id && (
                    <TextField
                      fullWidth
                      defaultValue={state.formState.name}
                      inputRef={state.form.fields.name.ref}
                      onChange={eventHandlers.handleOnInputChange}
                      name={INPUT_NAMES.NAME}
                      error={!!state.error}
                      helperText={state.error}
                      inputProps={{ style: { padding: 8 } }}
                      data-testid="new-domain-field"
                      disabled={isReadonly}
                    />
                  )}
                  {dataItem.id && <TableText dataText={dataItem.name} truncated dataTestId="domain-field" />}
                </Cell>
              )}
            />
            <DataTableColumn
              title={formatMessage('sender')}
              cell={({ dataItem }: { dataItem: BulkEmailExemptPolicy }) => (
                <Cell>
                  {!dataItem.id && <>{state.newEmailTypeRadios.get(AvailableSettings.BULK_EMAIL_SENDER_EXEMPT)}</>}
                  {dataItem.id && dataItem.type === AvailableSettings.BULK_EMAIL_SENDER_EXEMPT && (
                    <Radio checked defaultValue="" size="small" style={{ pointerEvents: 'none' }} color="primary" />
                  )}
                  {dataItem.id && dataItem.type === AvailableSettings.BULK_EMAIL_RCPT_EXEMPT && (
                    <Radio disabled defaultValue="" size="small" style={{ pointerEvents: 'none' }} />
                  )}
                </Cell>
              )}
            />
            <DataTableColumn
              title={formatMessage('recipient')}
              cell={({ dataItem }: { dataItem: BulkEmailExemptPolicy }) => (
                <Cell>
                  {!dataItem.id && <>{state.newEmailTypeRadios.get(AvailableSettings.BULK_EMAIL_RCPT_EXEMPT)}</>}
                  {dataItem.id && dataItem.type === AvailableSettings.BULK_EMAIL_RCPT_EXEMPT && (
                    <Radio checked defaultValue="" size="small" style={{ pointerEvents: 'none' }} color="primary" />
                  )}
                  {dataItem.id && dataItem.type === AvailableSettings.BULK_EMAIL_SENDER_EXEMPT && (
                    <Radio disabled defaultValue="" size="small" style={{ pointerEvents: 'none' }} />
                  )}
                </Cell>
              )}
            />
            <DataTableColumn
              sortable
              field={INPUT_NAMES.COMMENT}
              title={formatMessage('comment')}
              cell={({ dataItem }: { dataItem: BulkEmailExemptPolicy }) => (
                <Cell data-testid="comment-cell" className={inboundClasses.smallCell}>
                  {!dataItem.id && (
                    <TextField
                      fullWidth
                      defaultValue={state.formState.comment}
                      inputRef={state.form.fields.comment.ref}
                      onChange={eventHandlers.handleOnInputChange}
                      name={INPUT_NAMES.COMMENT}
                      inputProps={{ style: { padding: 8 } }}
                      data-testid="new-comment-field"
                      disabled={isReadonly}
                    />
                  )}
                  {dataItem.id && <TableText dataText={dataItem.comment} truncated dataTestId="comment-field" />}
                </Cell>
              )}
            />
            <DataTableColumn
              field="actions"
              title={formatMessage('actions')}
              cell={({ dataItem }: { dataItem: BulkEmailExemptPolicy }) => (
                <Cell data-testid="action-cell" className={inboundClasses.smallCell}>
                  {!dataItem.id && (
                    <Button
                      color="primary"
                      onClick={() => {
                        eventHandlers.onAdd()
                      }}
                      variant="text"
                      data-testid="add-button"
                      isLoading={state.isLoading}
                      disabled={isReadonly}
                    >
                      {formatMessage('add')}
                    </Button>
                  )}
                  {dataItem.id && (
                    <Button
                      color="primary"
                      onClick={() => {
                        eventHandlers.onRemove(dataItem)
                      }}
                      variant="text"
                      data-testid="remove-button"
                      isLoading={state.isLoading}
                      disabled={isReadonly}
                    >
                      {formatMessage('remove')}
                    </Button>
                  )}
                </Cell>
              )}
            />
          </DataTable>
          {!!state.itemToRemove && (
            <WarningDialog
              title={formatMessage('confirm_removal')}
              onConfirm={eventHandlers.onConfirmRemove}
              onCancel={eventHandlers.onConfirmCancel}
            />
          )}
        </Grid>
      </Grid>
    ),
    [
      formatMessage,
      eventHandlers,
      state,
      isReadonly,
      inboundClasses.smallCell,
      formatInboundMessage,
      inboundClasses.bulkEdit
    ]
  )
}
export default BulkEmailExempt
